import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser, useClerk } from '@clerk/clerk-react';
import { Button } from '../components/ui/button';
import { Check, CreditCard, LogOut, ArrowRight } from 'lucide-react';
import axios from 'axios';
import API_BASE_URL from '../config/api';

const CompleteSubscriptionPage = () => {
  const { user } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isDashboardLoading, setIsDashboardLoading] = useState(false);
  const [error, setError] = useState(null);
  const [billingInterval, setBillingInterval] = useState('month'); // 'month' or 'year'
  
  const backendUrl = API_BASE_URL;

  // Pricing configuration
  const pricing = {
    month: { price: 49.99, label: '/month', buttonText: '$49.99/mo' },
    year: { price: 549, label: '/year', buttonText: '$549/yr', savings: '1 month free' }
  };

  // FIX 2: Removed iOS restriction - this page now shows the welcome message
  // with "Go to Dashboard" as primary CTA. iOS users can see this page
  // since it's just informational (no in-app purchase happens here).

  const handleCompleteSubscription = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Get the base URL for redirects
      const baseUrl = window.location.origin;
      
      console.log('[CompleteSubscription] Creating checkout for user:', user.id);
      console.log('[CompleteSubscription] Email:', user.primaryEmailAddress?.emailAddress);
      console.log('[CompleteSubscription] Billing interval:', billingInterval);
      
      const response = await axios.post(
        `${backendUrl}/api/clerk/create-checkout`,
        {
          clerk_user_id: user.id,
          plan: 'pro',
          interval: billingInterval,
          email: user.primaryEmailAddress?.emailAddress,
          success_url: `${baseUrl}/dashboard?checkout=success&session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${baseUrl}/complete-subscription?checkout=cancelled`
        },
        {
          withCredentials: true,
          timeout: 15000
        }
      );
      
      console.log('[CompleteSubscription] Checkout response:', response.data);
      
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('[CompleteSubscription] Error creating checkout:', err);
      console.error('[CompleteSubscription] Error response:', err.response?.data);
      const errorMessage = err.response?.data?.detail || 'Unable to start checkout. Please try again.';
      setError(errorMessage);
      setIsLoading(false);
    }
  };

  // FIX: Call sync-user first to ensure trial is assigned, then navigate
  const handleGoToDashboard = async () => {
    if (!user) {
      navigate('/dashboard');
      return;
    }
    
    setIsDashboardLoading(true);
    try {
      console.log('[CompleteSubscription] Syncing user before dashboard redirect...');
      
      // Call sync-user to ensure trial is assigned
      await axios.post(
        `${backendUrl}/api/clerk/sync-user`,
        {
          clerk_user_id: user.id,
          email: user.primaryEmailAddress?.emailAddress,
          full_name: user.fullName || '',
          metadata: user.publicMetadata || {}
        },
        { withCredentials: true, timeout: 15000 }
      );

      // Reload Clerk user to pick up updated metadata (trial_end)
      await user.reload();
      console.log('[CompleteSubscription] Sync complete, navigating to dashboard');

      // Navigate to dashboard
      navigate('/dashboard');
    } catch (err) {
      console.error('[CompleteSubscription] Error syncing user:', err);
      // Navigate anyway — DashboardRoute will handle the sync
      navigate('/dashboard');
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const features = [
    "AI Coach - Daily personalized business insights",
    "Agent P&L Tracker - Real profit tracking",
    "Commission Cap Report - Automatic cap monitoring",
    "Action Tracker - Goal-to-activity conversion",
    "All Calculators - Mortgage, Net Sheet, Commission & more",
    "Branded PDFs - Professional client presentations",
    "Unlimited Saves - Store all your deals & calculations"
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Simple header */}
      <nav className="bg-white border-b border-gray-100">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_agent-portal-27/artifacts/azdcmpew_Logo_with_brown_background-removebg-preview.png" 
                alt="I Need Numbers" 
                className="h-7 sm:h-8 w-auto"
              />
              <span 
                className="text-base sm:text-lg font-bold text-[#2FA163]"
                style={{ fontFamily: 'Playfair Display, Georgia, serif' }}
              >
                I NEED NUMBERS
              </span>
            </div>
            
            <Button
              variant="ghost"
              onClick={handleSignOut}
              className="text-gray-600 hover:text-gray-900 text-sm sm:text-base px-2 sm:px-4"
            >
              <LogOut className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Sign Out</span>
            </Button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-16">
        <div className="max-w-2xl mx-auto text-center">
          {/* Welcome message - mobile responsive */}
          <h1 
            className="text-xl sm:text-2xl md:text-4xl font-bold text-gray-900 mb-2 sm:mb-4"
            style={{ fontFamily: 'Playfair Display, Georgia, serif' }}
          >
            Welcome to I Need Numbers!
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-4 sm:mb-8">
            You have 7 days of full PRO access — no credit card needed.
          </p>

          {/* Trial callout - mobile responsive */}
          <div className="mb-4 sm:mb-8 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl px-4 sm:px-6 py-3 sm:py-4">
            <div className="flex items-center justify-center gap-2 mb-1">
              <span className="text-xl sm:text-2xl">🎉</span>
              <span className="text-base sm:text-lg font-bold text-purple-700">Your Free Trial is Active!</span>
            </div>
            <p className="text-gray-600 text-xs sm:text-sm">
              Explore all PRO features. Subscribe anytime to keep access after your trial.
            </p>
          </div>

          {/* Primary CTA - Go to Dashboard */}
          <Button
            onClick={handleGoToDashboard}
            disabled={isDashboardLoading}
            className="w-full bg-[#2FA163] hover:bg-[#268a54] text-white text-base sm:text-lg py-4 sm:py-6 font-semibold mb-4"
            size="lg"
            data-testid="go-to-dashboard-btn"
          >
            {isDashboardLoading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                Setting up your account...
              </>
            ) : (
              <>
                <ArrowRight className="w-5 h-5 mr-2" />
                Go to Dashboard
              </>
            )}
          </Button>

          {/* Pricing card - mobile responsive */}
          <div className="bg-gray-50 rounded-2xl p-4 sm:p-8 mb-4 sm:mb-8 border border-gray-100">
            <p className="text-gray-500 text-xs sm:text-sm mb-3 sm:mb-4">Ready to subscribe now?</p>
            
            {/* Billing toggle */}
            <div className="flex items-center justify-center gap-3 mb-4 sm:mb-6">
              <span className={`text-sm font-medium ${billingInterval === 'month' ? 'text-gray-900' : 'text-gray-500'}`}>
                Monthly
              </span>
              <button
                onClick={() => setBillingInterval(billingInterval === 'month' ? 'year' : 'month')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  billingInterval === 'year' ? 'bg-[#2FA163]' : 'bg-gray-300'
                }`}
                data-testid="billing-toggle"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    billingInterval === 'year' ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className={`text-sm font-medium ${billingInterval === 'year' ? 'text-gray-900' : 'text-gray-500'}`}>
                Annual
              </span>
              {billingInterval === 'year' && (
                <span className="bg-[#2FA163] text-white text-xs px-2 py-0.5 rounded-full font-medium">
                  1 month free
                </span>
              )}
            </div>
            
            <div className="mb-4 sm:mb-6">
              <span className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900">
                ${billingInterval === 'year' ? '549' : '49.99'}
              </span>
              <span className="text-lg sm:text-xl text-gray-500">
                {billingInterval === 'year' ? '/year' : '/month'}
              </span>
              {billingInterval === 'year' && (
                <p className="text-sm text-[#2FA163] mt-1">Save $50.89 compared to monthly</p>
              )}
            </div>

            {/* Features list */}
            <ul className="text-left space-y-2 sm:space-y-3 mb-6 sm:mb-8">
              {features.map((feature, index) => (
                <li key={index} className="flex items-start">
                  <Check className="w-4 sm:w-5 h-4 sm:h-5 text-[#2FA163] mr-2 sm:mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 text-sm sm:text-base">{feature}</span>
                </li>
              ))}
            </ul>

            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">
                {error}
              </div>
            )}

            <Button
              onClick={handleCompleteSubscription}
              disabled={isLoading}
              variant="outline"
              className="w-full border-[#2FA163] text-[#2FA163] hover:bg-[#2FA163] hover:text-white text-base sm:text-lg py-4 sm:py-6 font-semibold"
              size="lg"
              data-testid="subscribe-now-btn"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current mr-2" />
                  Redirecting to checkout...
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5 mr-2" />
                  Subscribe Now ({billingInterval === 'year' ? '$549/yr' : '$49.99/mo'})
                </>
              )}
            </Button>
          </div>

          <p className="text-xs sm:text-sm text-gray-500">
            Secure payment powered by Stripe. Cancel anytime from your account settings.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CompleteSubscriptionPage;
